import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import { HeartPulse, Plus, Trash2, AlertTriangle, Filter } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import MedicalConditionPicker from "./MedicalConditionPicker";
import { DateDMYInput } from "@/components/DateDMYInput";

const STATUS_STYLES = {
  active:     "bg-[#FDF3ED] text-[#B0521F]",
  controlled: "bg-[#E8F0EB] text-[#2D6A4F]",
  chronic:    "bg-[#EEE5F0] text-[#6B3F72]",
  resolved:   "bg-linen text-ink-mid",
  inactive:   "bg-linen text-ink-soft",
};

const STATUS_DOT = {
  active:     "bg-[#D06A42] border-[#D06A42]",
  controlled: "bg-pine border-pine",
  chronic:    "bg-[#8A5A9B] border-[#8A5A9B]",
  resolved:   "bg-white border-pine-soft",
  inactive:   "bg-[#E2DCD0] border-[#E2DCD0]",
};

const STATUSES = [
  { value: "active",     label: "Active" },
  { value: "controlled", label: "Controlled" },
  { value: "chronic",    label: "Chronic" },
  { value: "resolved",   label: "Resolved" },
  { value: "inactive",   label: "Inactive" },
];

const emptyForm = () => ({
  selected: null,     // full condition object from picker
  diagnosed_on: "",
  status: "active",
  medications: "",
  notes: "",
});

export default function MedicalHistoryCard({ patient, onReload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const history = useMemo(() => {
    let list = [...(patient?.medical_history || [])].sort((a, b) => {
      const av = a.diagnosed_on || a.created_at || "";
      const bv = b.diagnosed_on || b.created_at || "";
      return bv.localeCompare(av);
    });
    if (statusFilter !== "all") list = list.filter((h) => h.status === statusFilter);
    if (categoryFilter !== "all") list = list.filter((h) => (h.category || "custom") === categoryFilter);
    return list;
  }, [patient?.medical_history, statusFilter, categoryFilter]);

  const availableCategories = useMemo(() => {
    const set = new Set();
    (patient?.medical_history || []).forEach((h) => set.add(h.category || "custom"));
    return Array.from(set);
  }, [patient?.medical_history]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.selected) {
      toast.error("Please select or add a medical condition");
      return;
    }
    try {
      const payload = {
        condition: form.selected.name || form.selected.condition,
        condition_slug: form.selected.slug || null,
        category: form.selected.category || null,
        is_custom: !!form.selected.is_custom,
        diagnosed_on: form.diagnosed_on || null,
        status: form.status,
        medications: form.medications || null,
        notes: form.notes || null,
      };
      await api.post(`/patients/${patient.id}/medical-history`, payload);
      toast.success("Medical history entry added");
      setOpen(false);
      setForm(emptyForm());
      onReload?.();
    } catch (err) { toast.error(apiError(err, "Failed")); }
  };

  const remove = async (entryId) => {
    if (!confirm("Remove this medical history entry?")) return;
    try {
      await api.delete(`/patients/${patient.id}/medical-history/${entryId}`);
      toast.success("Removed");
      onReload?.();
    } catch (err) { toast.error(apiError(err, "Failed")); }
  };

  const selected = form.selected;

  return (
    <div className="card-widget" data-testid="medical-history-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <HeartPulse className="w-4 h-4 text-ink-mid" />
          <div className="overline text-ink-soft">Medical history</div>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(emptyForm()); }}>
          <DialogTrigger asChild>
            <button data-testid="mh-add-btn" className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-pine/20 hover:bg-linen">
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-2xl">New medical history entry</DialogTitle></DialogHeader>
            <form onSubmit={add} className="space-y-4" data-testid="mh-form">
              {/* Standardized condition picker */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Condition</label>
                <MedicalConditionPicker
                  value={selected}
                  onChange={(c) => setForm((p) => ({ ...p, selected: c }))}
                />
              </div>

              {/* Smart warnings */}
              {selected?.warnings?.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-1.5"
                     data-testid="mh-condition-warnings">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-amber-800 font-semibold">
                    <AlertTriangle className="w-3.5 h-3.5" /> Clinical alerts
                  </div>
                  {selected.warnings.map((w, i) => (
                    <div key={i} className="text-xs text-amber-900 flex items-start gap-1.5">
                      <span className="mt-0.5">⚠</span><span>{w}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Diagnosed on</label>
                  <DateDMYInput data-testid="mh-diagnosed"
                    value={form.diagnosed_on} onChange={(e) => setForm({ ...form, diagnosed_on: e.target.value })}
                    className="w-full rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm font-mono" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Status</label>
                  <select data-testid="mh-status" value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm bg-white">
                    {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              <input data-testid="mh-medications" placeholder="Current medications (optional)"
                value={form.medications} onChange={(e) => setForm({ ...form, medications: e.target.value })}
                className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
              <textarea data-testid="mh-notes" placeholder="Notes (optional)" rows={2}
                value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm resize-none" />
              <button data-testid="mh-submit" className="w-full btn-primary text-sm">Save entry</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      {(patient?.medical_history || []).length > 1 && (
        <div className="flex flex-wrap items-center gap-2 mb-4 text-xs" data-testid="mh-filters">
          <Filter className="w-3 h-3 text-ink-soft" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  data-testid="mh-filter-status"
                  className="rounded-full border border-[#E8EAE9] px-3 py-1 bg-white text-ink-mid">
            <option value="all">All statuses</option>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          {availableCategories.length > 1 && (
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                    data-testid="mh-filter-category"
                    className="rounded-full border border-[#E8EAE9] px-3 py-1 bg-white text-ink-mid">
              <option value="all">All categories</option>
              {availableCategories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>
      )}

      {history.length === 0 ? (
        <div className="text-sm text-ink-soft text-center py-6">No medical history recorded.</div>
      ) : (
        <div className="relative">
          <div className="absolute left-3 top-2 bottom-2 w-px bg-[#E8EAE9]" />
          <div className="space-y-4">
            {history.map((h) => (
              <div key={h.id} className="relative pl-10 group" data-testid={`mh-entry-${h.id}`}>
                <div className={`absolute left-1.5 top-1 w-4 h-4 rounded-full border-2 ${
                  STATUS_DOT[h.status] || STATUS_DOT.active
                }`} />
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-ink">{h.condition}</span>
                      {h.category && (
                        <Badge variant="outline" className="text-[10px] border-pine/20 text-ink-mid">
                          {h.category}
                        </Badge>
                      )}
                      {h.is_custom && (
                        <Badge variant="outline" className="text-[10px] border-pine/20 text-ink-soft">
                          Custom
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-ink-mid mt-0.5">
                      {h.diagnosed_on ? `Diagnosed ${h.diagnosed_on}` : "Date not recorded"}
                      {h.medications && ` · on ${h.medications}`}
                    </div>
                    {h.notes && <div className="text-xs text-ink-mid mt-1 italic">&ldquo;{h.notes}&rdquo;</div>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest ${STATUS_STYLES[h.status] || STATUS_STYLES.active}`}>
                      {h.status}
                    </span>
                    <button data-testid={`mh-del-${h.id}`} onClick={() => remove(h.id)}
                      className="text-ink-disabled hover:text-[#B0521F] opacity-0 group-hover:opacity-100 transition-opacity p-1">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
